import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useBooking } from "../context/BookingContext";
import { IMAGES } from "../lib/site";

const Hero = () => {
  const { t } = useLang();
  const { setOpen } = useBooking();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden grain"
    >
      {/* Background image with parallax/zoom */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 hero-zoom bg-center bg-cover"
          style={{ backgroundImage: `url(${IMAGES.heroBg})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/55 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.12),transparent_60%)]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-40 md:pt-48 pb-24 md:pb-32 min-h-screen flex flex-col justify-between">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-gold" />
            <span className="label-eyebrow text-gold" data-testid="hero-eyebrow">
              {t.hero.eyebrow}
            </span>
          </motion.div>

          <h1 className="font-display uppercase leading-[0.92] tracking-tighter text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl">
            {[t.hero.title1, t.hero.title2, t.hero.title3].map((line, i) => {
              const renderLine = () => {
                if (i !== 2) return line;
                const trimmed = line.trim();
                const firstSpace = trimmed.indexOf(" ");
                if (firstSpace === -1) {
                  return (
                    <span className="text-gold italic font-display">
                      {trimmed}
                    </span>
                  );
                }
                const first = trimmed.slice(0, firstSpace);
                const rest = trimmed.slice(firstSpace + 1);
                return (
                  <span>
                    <span className="text-gold italic font-display">
                      {first}{" "}
                    </span>
                    <span>{rest}</span>
                  </span>
                );
              };
              return (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 + i * 0.12, ease: "easeOut" }}
                  className="block"
                >
                  {renderLine()}
                </motion.span>
              );
            })}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-8 max-w-xl text-base md:text-lg text-white/70 leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => setOpen(true)}
              data-testid="hero-book-btn"
              className="group inline-flex items-center gap-2 bg-gold text-black px-7 py-4 rounded-sm font-display uppercase tracking-tight text-sm md:text-base hover:scale-[1.03] glow-gold-hover transition-all"
            >
              {t.hero.cta}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <a
              href="#services"
              data-testid="hero-services-btn"
              className="inline-flex items-center gap-2 border border-white/20 text-white px-7 py-4 rounded-sm label-eyebrow hover:bg-white/5 hover:border-gold/50 transition-all"
            >
              {t.hero.ctaSecondary}
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="hidden md:flex items-end justify-between mt-16"
        >
          <a
            href="#about"
            className="flex items-center gap-3 text-white/50 hover:text-gold transition-colors"
            data-testid="hero-scroll"
          >
            <ArrowDown className="h-4 w-4 animate-bounce" />
            <span className="label-eyebrow">{t.hero.scroll}</span>
          </a>
          <div className="flex items-center gap-8 text-white/60 text-xs label-eyebrow">
            <span>Ii · Yliranta</span>
            <span className="h-px w-8 bg-white/20" />
            <span>EST. TTcuts</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
